import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { BACKEND_URL, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import MediaEditor from "./MediaEditor";
import {
  Wand2, Play, Film, Image as ImageIcon, MessageSquare, Send, Check, Trash2, Clock, Loader2, ChevronRight, X, Upload,
} from "lucide-react";

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

function fmtRel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtTs(t) {
  if (!isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const r = Math.floor(t % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/**
 * Studio panel that lives inside a DealRoom. Shows the creator's Studio drafts
 * linked to this hire + timestamped brand review comments.
 *
 * Roles:
 *  - Creator: can open a linked draft in the editor to make revisions
 *  - Brand: can view + preview drafts + drop timestamped comments
 */
export default function HireStudioPanel({ hireId, role, hire }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [editorState, setEditorState] = useState(null); // { file, draftId }
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const [videoTime, setVideoTime] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/studio/hires/${hireId}/drafts`);
      const rows = r.data?.drafts || [];
      setDrafts(rows);
      // Pick the most recent draft to preview by default
      if (rows.length > 0 && !previewDraft) {
        setPreviewDraft(rows[0]);
        await loadComments(rows[0].id);
      }
    } catch (e) {
      // 403 means user isn't on this hire — hide panel gracefully
    } finally { setLoading(false); }
  };

  const loadComments = async (draftId) => {
    try {
      const r = await api.get(`/studio/drafts/${draftId}/comments`);
      setComments(r.data?.comments || []);
    } catch (e) {
      setComments([]);
    }
  };

  useEffect(() => { load(); }, [hireId]);

  const selectDraft = async (d) => {
    setPreviewDraft(d);
    setVideoTime(0);
    await loadComments(d.id);
  };

  const openInEditor = async (draft) => {
    if (!draft.source_url || draft.source_url.startsWith("local://")) {
      toast.error("Draft has no persisted source file");
      return;
    }
    setBusy(true);
    try {
      const resp = await fetch(toAbsolute(draft.source_url));
      if (!resp.ok) throw new Error(`Fetch failed ${resp.status}`);
      const blob = await resp.blob();
      const type = blob.type || (draft.kind === "video" ? "video/mp4" : "image/jpeg");
      const ext = type.includes("mp4") ? "mp4" : type.includes("webm") ? "webm" : type.includes("png") ? "png" : (draft.kind === "video" ? "mp4" : "jpg");
      const file = new File([blob], draft.title || `draft.${ext}`, { type });
      setEditorState({ file, draftId: draft.id });
    } catch (e) {
      toast.error("Couldn't load draft source");
    } finally { setBusy(false); }
  };

  const linkNewDraft = async (file) => {
    if (!file || !hireId) return;
    // Open editor with hireId so save flow links the draft automatically
    setEditorState({ file, hireId });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeEditor = () => {
    setEditorState(null);
    load();
  };

  const addComment = async () => {
    if (!previewDraft || !commentText.trim()) return;
    const ts = videoRef.current?.currentTime ?? videoTime ?? 0;
    setAddingComment(true);
    try {
      const r = await api.post(`/studio/drafts/${previewDraft.id}/comments`, {
        timestamp: Math.max(0, Number(ts) || 0),
        text: commentText.trim(),
      });
      setComments((rows) => [...rows, r.data]);
      setCommentText("");
      toast.success(`Comment left at ${fmtTs(ts)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Comment failed");
    } finally { setAddingComment(false); }
  };

  const resolveComment = async (c) => {
    try {
      await api.patch(`/studio/drafts/${previewDraft.id}/comments/${c.id}/resolve`);
      setComments((rows) => rows.map((r) => (r.id === c.id ? { ...r, resolved: true } : r)));
    } catch (e) {
      toast.error("Failed to resolve");
    }
  };

  const deleteComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/studio/drafts/${previewDraft.id}/comments/${c.id}`);
      setComments((rows) => rows.filter((r) => r.id !== c.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const seekTo = (t) => {
    if (videoRef.current) {
      try { videoRef.current.currentTime = t; } catch { /* noop */ }
    }
  };

  if (loading && drafts.length === 0) {
    return (
      <section className="p-6 rounded-2xl bg-[#0C0C10] hairline" data-testid="hire-studio-panel-loading">
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Studio drafts…
        </div>
      </section>
    );
  }

  // For creators with no draft yet — show CTA to open Studio
  if (drafts.length === 0 && role === "creator") {
    return (
      <section className="p-6 rounded-2xl bg-gradient-to-br from-nexus-pink/10 via-fuchsia-500/5 to-nexus-blue/10 border border-white/10" data-testid="hire-studio-empty-creator">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-nexus-pink/15 flex items-center justify-center shrink-0">
            <Wand2 className="h-5 w-5 text-nexus-pink" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-nexus-pink mb-1">Creator Studio</div>
            <h3 className="font-display font-bold text-xl mb-2">Edit your delivery in Studio</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Upload a clip, edit it right here in Nexus, and share the draft with {hire?.brand_name || "the brand"}
              — they can leave timestamped comments before you finalise.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => linkNewDraft(e.target.files?.[0])}
              data-testid="hire-studio-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold"
              data-testid="hire-studio-open-btn"
            >
              <Upload className="h-4 w-4 mr-2" /> Open in Studio
            </Button>
          </div>
        </div>
        {editorState && (
          <MediaEditor
            file={editorState.file}
            draftId={editorState.draftId || null}
            hireId={editorState.hireId || hireId}
            onCancel={closeEditor}
            onExport={closeEditor}
            onDraftSaved={() => load()}
          />
        )}
      </section>
    );
  }

  // For brands with no drafts yet
  if (drafts.length === 0 && role === "brand") {
    return (
      <section className="p-6 rounded-2xl bg-[#0C0C10] hairline text-center" data-testid="hire-studio-empty-brand">
        <Wand2 className="h-6 w-6 mx-auto mb-3 text-nexus-pink" />
        <div className="font-display text-lg mb-1">No Studio drafts yet</div>
        <p className="text-sm text-zinc-500">
          {hire?.creator_name || "The creator"} hasn&apos;t shared a Studio draft for this collab yet. Once they do, you can leave timestamped feedback here.
        </p>
      </section>
    );
  }

  return (
    <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-5" data-testid="hire-studio-panel">
      {editorState && (
        <MediaEditor
          file={editorState.file}
          draftId={editorState.draftId || null}
          hireId={editorState.hireId || hireId}
          onCancel={closeEditor}
          onExport={closeEditor}
          onDraftSaved={() => load()}
        />
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-nexus-pink" />
          <div className="text-xs uppercase tracking-widest text-zinc-500">Studio drafts · {drafts.length}</div>
        </div>
        {role === "creator" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => linkNewDraft(e.target.files?.[0])}
              data-testid="hire-studio-add-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="nexus-gradient text-white border-0 rounded-full h-8 px-3 font-semibold text-[11px] uppercase tracking-widest"
              data-testid="hire-studio-add-btn"
            >
              <Upload className="h-3 w-3 mr-1.5" /> Add draft
            </Button>
          </>
        )}
      </div>

      {/* Draft strip */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="hire-studio-drafts-strip">
        {drafts.map((d) => {
          const active = previewDraft?.id === d.id;
          const abs = toAbsolute(d.thumb_url || d.source_url);
          const video = d.kind === "video";
          return (
            <button
              key={d.id}
              onClick={() => selectDraft(d)}
              className={`shrink-0 relative h-16 w-24 rounded-lg overflow-hidden border-2 transition-all ${active ? "border-nexus-pink" : "border-white/10 hover:border-white/25"}`}
              data-testid={`hire-studio-draft-thumb-${d.id}`}
            >
              {video ? (
                <video src={abs} className="w-full h-full object-cover" muted preload="metadata" />
              ) : (
                <img src={abs} alt="" className="w-full h-full object-cover" />
              )}
              {d.unresolved_comments > 0 && (
                <span className="absolute top-1 right-1 bg-nexus-pink text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
                  {d.unresolved_comments}
                </span>
              )}
              <span className="absolute bottom-0 left-0 right-0 text-[9px] uppercase tracking-widest text-white bg-black/70 px-1 py-0.5 flex items-center gap-1">
                {video ? <Film className="h-2.5 w-2.5" /> : <ImageIcon className="h-2.5 w-2.5" />}
                {video ? "Video" : "Image"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Preview + comments */}
      {previewDraft && (
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3 space-y-3">
            <div className="rounded-xl overflow-hidden bg-black aspect-video">
              {previewDraft.kind === "video" ? (
                <video
                  ref={videoRef}
                  src={toAbsolute(previewDraft.source_url)}
                  controls
                  className="w-full h-full object-contain"
                  onTimeUpdate={(e) => setVideoTime(e.currentTarget.currentTime || 0)}
                  data-testid="hire-studio-preview-video"
                />
              ) : (
                <img
                  src={toAbsolute(previewDraft.source_url)}
                  alt=""
                  className="w-full h-full object-contain"
                  data-testid="hire-studio-preview-image"
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-zinc-500">
                {previewDraft.title || "Untitled draft"} · Updated {fmtRel(previewDraft.updated_at)}
              </div>
              {role === "creator" && (
                <Button
                  onClick={() => openInEditor(previewDraft)}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full border-white/10 text-white hover:bg-white/5 h-8"
                  data-testid="hire-studio-edit-btn"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
                  Edit in Studio
                </Button>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="lg:col-span-2 flex flex-col gap-3 min-h-[280px]" data-testid="hire-studio-comments">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-nexus-pink" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">Timestamped feedback</div>
            </div>
            {comments.length === 0 ? (
              <div className="text-[12px] text-zinc-500 italic bg-white/[0.02] rounded-xl p-4 flex-1 flex items-center justify-center border border-dashed border-white/10">
                No comments yet.{" "}
                {role === "brand" ? "Scrub the video and drop a note at the exact moment." : "The brand hasn't left feedback yet."}
              </div>
            ) : (
              <ol className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className={`p-3 rounded-lg bg-white/[0.02] border border-white/5 ${c.resolved ? "opacity-60" : ""}`}
                    data-testid={`hire-studio-comment-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <button
                        onClick={() => seekTo(c.timestamp)}
                        className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                          c.author_role === "brand" ? "bg-nexus-blue/15 text-nexus-blue" : "bg-nexus-pink/15 text-nexus-pink"
                        }`}
                        data-testid={`hire-studio-comment-seek-${c.id}`}
                      >
                        @ {fmtTs(c.timestamp)}
                      </button>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5" /> {fmtRel(c.created_at)}
                      </span>
                    </div>
                    <div className="text-[11px] text-zinc-400 mb-1">
                      <span className="font-semibold text-white">{c.author_name}</span> · <span className="uppercase tracking-widest">{c.author_role}</span>
                    </div>
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap">{c.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {!c.resolved && (
                        <button
                          onClick={() => resolveComment(c)}
                          className="text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-100 flex items-center gap-1"
                          data-testid={`hire-studio-comment-resolve-${c.id}`}
                        >
                          <Check className="h-3 w-3" /> Resolve
                        </button>
                      )}
                      {c.resolved && (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
                          <Check className="h-3 w-3" /> Resolved
                        </span>
                      )}
                      {c.author_role === role && (
                        <button
                          onClick={() => deleteComment(c)}
                          className="text-[10px] uppercase tracking-widest text-red-300 hover:text-red-100 flex items-center gap-1 ml-auto"
                          data-testid={`hire-studio-comment-delete-${c.id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
            <div className="pt-2 border-t border-white/5 space-y-2">
              <div className="flex items-center justify-between text-[11px] text-zinc-500">
                <span>Comment @</span>
                <span className="tabular-nums text-white font-semibold">{fmtTs(videoTime)}</span>
              </div>
              <div className="flex gap-2">
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value.slice(0, 1000))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); }
                  }}
                  placeholder={role === "brand" ? "Tighten the pacing here…" : "Reply to feedback…"}
                  className="flex-1 bg-white/[0.02] border border-white/10 rounded-full px-3 h-9 text-white text-sm"
                  data-testid="hire-studio-comment-input"
                />
                <Button
                  onClick={addComment}
                  disabled={addingComment || !commentText.trim()}
                  className="nexus-gradient text-white border-0 rounded-full h-9 px-3 font-semibold disabled:opacity-60"
                  data-testid="hire-studio-comment-submit"
                >
                  {addingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
